import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { VoteStatus } from "types/voteEnum";

import { ValidateButton } from "components/common/ValidateButton";
import { Input } from "components/common/Input";
import { Alert, AlertSuccess } from "components/common/Alert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

type Props = {
    successMessage?: string;
    handleSubmit: (email: string, password: string) => Promise<void>;
    defaultEmailValue?: string;
    disabled?: boolean;
};

const EmailPasswordForm = ({
    successMessage,
    handleSubmit,
    defaultEmailValue,
    disabled,
}: Props) => {
    const { t } = useTranslation();

    const [status, setStatus] = useState(VoteStatus.Idle);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (emailRef.current && defaultEmailValue) {
            emailRef.current.value = defaultEmailValue;
        }
    }, [defaultEmailValue]);

    const successAlert =
        successMessage && !errorMessage ? (
            <AlertSuccess>{successMessage}</AlertSuccess>
        ) : null;

    return (
        <form
            method="post"
            className="form panel-body"
            onSubmit={(event) => {
                event.preventDefault();

                if (!emailRef.current || !passwordRef.current) {
                    return;
                }

                setStatus(VoteStatus.PendingValidation);

                const emailValue = emailRef.current.value;
                const passwordValue = passwordRef.current.value;

                handleSubmit(emailValue, passwordValue).catch(
                    (errorMessageParam) => {
                        setErrorMessage(errorMessageParam);
                        setStatus(VoteStatus.Error);
                    },
                );
            }}
        >
            {errorMessage && <AlertError>{errorMessage}</AlertError>}
            {/* Always in the page, so the message is announced (RGAA 7.5) */}
            <div role="status">{successAlert}</div>
            <div className="form-group">
                <StyledInput
                    ref={emailRef}
                    type="email"
                    className="form-control"
                    name="email"
                    autoComplete="off"
                    placeholder={`${t("email")} (*)`}
                    aria-label={t("email")}
                    required
                    disabled={disabled}
                />
            </div>
            <div className="form-group">
                <StyledInput
                    ref={passwordRef}
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    name="password"
                    autoComplete="off"
                    placeholder={`${t("password")} (*)`}
                    aria-label={t("password")}
                    style={{
                        paddingRight: "35px",
                    }}
                    required
                    disabled={disabled}
                />
                <StyledInputGroup className="input-group-append">
                    <StyledIconButton
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={disabled}
                    >
                        {showPassword ? (
                            <VisibilityOffIcon />
                        ) : (
                            <VisibilityIcon />
                        )}
                    </StyledIconButton>
                </StyledInputGroup>
            </div>
            <StyledButton
                disabled={disabled || VoteStatus.PendingValidation === status}
                type="submit"
                className="btn btn-primary btn-full"
            >
                {`${t("joinIn")}`}
            </StyledButton>
        </form>
    );
};

const StyledInput = styled(Input)`
    &::-ms-reveal,
    &::-ms-clear {
        display: none;
    }
`;

const StyledButton = styled(ValidateButton)``;

const AlertError = styled(Alert)``;

const StyledInputGroup = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const StyledIconButton = styled.button`
    position: absolute;
    right: 1px;
    bottom: 5px;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #495057;
`;

export default EmailPasswordForm;
